// Global Instructions Rule Applied!
//
// Agent signal card — the pilot-review format (approved deviation, see
// docs/FRONTEND_DESIGN_PATTERNS.md "Approved deviations"): mono S-ref chip,
// 4px severity left border, tinted severity pill, kind label + source-interaction
// date, right-aligned confidence/fired date, serif-italic evidence blockquotes
// with citation lines.
// Actions are MAINTENANCE, not workflow: "Got it" (acknowledged) / "Dismiss".
// Agent-written content only — never reuse for human-authored data.
import React, { useState } from 'react'
import { Button } from 'antd'
import dayjs from 'dayjs'
import { AgentSignal } from '../../../state_manager/data_models/internal_agents_model.ts'
import { interactionOpenTarget, TimelineRef } from '../utils/interactionOpenTarget.ts'

// Exported so the section's COLLAPSED summary rows carry the same severity
// vocabulary as the cards they fold away — one map, not two (design patterns:
// colour is a status vocabulary, never re-typed per surface).
export const SEVERITY_STYLES: Record<string, { border: string; pillColor: string; pillBg: string }> = {
  crit: { border: '#AE372F', pillColor: '#AE372F', pillBg: '#FBF0EF' },
  warn: { border: '#9A6A10', pillColor: '#9A6A10', pillBg: '#FBF4E6' },
  info: { border: '#47617D', pillColor: '#47617D', pillBg: '#EEF2F7' }
}
const SID_ACCENT = '#3E6FA8'

interface AgentSignalCardProps {
  signal: AgentSignal
  // Per-company chronological index (S1, S2, …) — computed by the parent
  // over the company's full signal list so refs stay stable.
  sequenceNumber: number
  onAcknowledge?: (signalId: string) => void
  onDismiss?: (signalId: string) => void
  /** Opens the pre-filled task form. Human-triggered by design. */
  onCreateTask?: (signal: AgentSignal) => void
  /** Opens a cited interaction on its timeline; the card only renders a link
   *  where `interactionOpenTarget` resolves one. */
  onOpenInteraction?: (interactionId: string, timeline?: TimelineRef | null) => void
  onOpenRun?: (runId: string) => void
  // Dashboard strip shows one quote; the Agent tab shows all.
  maxEvidence?: number
}

export const AgentSignalCard: React.FC<AgentSignalCardProps> = ({
  signal,
  sequenceNumber,
  onAcknowledge,
  onDismiss,
  onCreateTask,
  onOpenInteraction,
  onOpenRun,
  maxEvidence = 1
}) => {
  const [expanded, setExpanded] = useState(false)
  const severity = SEVERITY_STYLES[signal.agent_signals__severity] ?? SEVERITY_STYLES.info
  const evidence = expanded ? signal.evidence : signal.evidence.slice(0, maxEvidence)
  const hiddenCount = signal.evidence.length - evidence.length
  // Evidence arrives newest-first from view__agent_signals — the first dated
  // item is the most recent interaction the signal was drawn from.
  const sourceInteractionDate =
    signal.evidence.find((item) => item.interactions__datetime)?.interactions__datetime ?? null
  const isOpen = signal.agent_signals__status === 'open'
  // Only surfaced while the task is live — the view joins it that way, so a
  // deleted task quietly stops being shown.
  const linkedTask = signal.agent_signals__task_id ? signal : null

  return (
    <article
      data-component='AgentSignalCard'
      className='bg-white rounded-lg border border-gray-100 px-4 py-3'
      style={{ borderLeftWidth: 4, borderLeftColor: severity.border }}
    >
      <div className='flex items-center gap-2.5 flex-wrap'>
        <span
          className='font-mono font-bold text-xs rounded px-1.5'
          style={{ color: SID_ACCENT, border: `1px solid ${SID_ACCENT}` }}
        >
          S{sequenceNumber}
        </span>
        <span
          className='text-[10.5px] font-bold tracking-wider rounded-full px-2 py-0.5 uppercase'
          style={{ color: severity.pillColor, background: severity.pillBg }}
        >
          {signal.agent_signals__severity}
        </span>
        <span className='text-gray-500 text-xs'>{signal.kind_label}</span>
        {sourceInteractionDate ? (
          <span className='text-gray-400 text-xs tabular-nums'>
            · {dayjs(sourceInteractionDate).format('MM/DD/YYYY')}
          </span>
        ) : null}
        <span className='ml-auto text-gray-400 text-[11.5px] tabular-nums'>
          {signal.agent_signals__confidence != null
            ? `confidence ${signal.agent_signals__confidence.toFixed(2)} · `
            : ''}
          fired {dayjs(signal.agent_signals__fired_at).format('MM/DD/YYYY')}
          {signal.agent_runs__id && onOpenRun ? (
            <>
              {' · '}
              <button
                type='button'
                className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
                onClick={() => onOpenRun(signal.agent_runs__id as string)}
              >
                run
              </button>
            </>
          ) : null}
        </span>
      </div>

      <h4 className='mt-2 mb-0.5 text-sm font-semibold'>{signal.agent_signals__title}</h4>
      {signal.agent_signals__summary ? (
        <p className='m-0 mb-1 text-gray-600'>{signal.agent_signals__summary}</p>
      ) : null}

      {evidence.map((item) => (
        <figure key={item.interactions__id} className='m-0 mt-2'>
          {item.excerpt ? (
            <blockquote
              className='m-0 rounded-r-md px-3 py-1.5 italic text-[13px]'
              style={{
                background: '#F1F3F6',
                borderLeft: '3px solid #d9d9d9',
                fontFamily: 'Charter, Georgia, "Times New Roman", serif'
              }}
            >
              {item.excerpt}
            </blockquote>
          ) : null}
          <figcaption className='text-gray-400 text-[11.5px] mt-0.5 ml-3'>
            {item.interactions__type ? `${item.interactions__type} · ` : ''}
            {onOpenInteraction &&
            interactionOpenTarget(
              signal.agent_signals__context_table,
              signal.agent_signals__context_id,
              item.interactions__id,
              { table: item.timeline_table, id: item.timeline_id }
            ) ? (
              <button
                type='button'
                className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
                onClick={() =>
                  onOpenInteraction(item.interactions__id, { table: item.timeline_table, id: item.timeline_id })
                }
              >
                {item.interactions__subject || 'interaction'}
              </button>
            ) : (
              <span>{item.interactions__subject || 'interaction'}</span>
            )}
            {item.interactions__datetime
              ? ` · ${dayjs(item.interactions__datetime).format('MM/DD/YYYY')}`
              : ''}
          </figcaption>
        </figure>
      ))}
      {hiddenCount > 0 ? (
        <button
          type='button'
          className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left text-[11.5px] mt-1 ml-3'
          onClick={() => setExpanded(true)}
        >
          + {hiddenCount} further cited {hiddenCount === 1 ? 'interaction' : 'interactions'}
        </button>
      ) : null}

      <div className='flex gap-1.5 mt-2 items-center'>
        {isOpen ? (
          <>
            {onAcknowledge ? (
              <Button size='small' onClick={() => onAcknowledge(signal.agent_signals__id)}>
                Got it
              </Button>
            ) : null}
            {/* Raising a task is the one action that turns a signal into work,
                and it is always a person's decision — the agent never creates
                tasks. Hidden once this signal already has one. */}
            {onCreateTask && !linkedTask ? (
              <Button size='small' onClick={() => onCreateTask(signal)}>
                Create task
              </Button>
            ) : null}
            {onDismiss ? (
              <Button size='small' danger ghost onClick={() => onDismiss(signal.agent_signals__id)}>
                Dismiss
              </Button>
            ) : null}
          </>
        ) : (
          <span className='text-gray-400 text-xs'>
            {signal.agent_signals__status}
            {signal.agent_signals__status_reason ? ` · ${signal.agent_signals__status_reason}` : ''}
          </span>
        )}
        {linkedTask ? (
          <span
            className='text-[11.5px] rounded px-1.5 py-0.5 flex items-center gap-1'
            style={{ color: '#3F6212', background: '#F2F7E9' }}
            title={signal.tasks__subject ?? undefined}
          >
            <span className='font-semibold uppercase tracking-wide text-[10px]'>
              {signal.tasks__completed ? 'Task done' : 'Task'}
            </span>
            <span className='max-w-[220px] truncate'>{signal.tasks__subject}</span>
            {signal.tasks__due_by ? (
              <span className='tabular-nums text-gray-500'>
                due {dayjs(signal.tasks__due_by).format('MM/DD/YYYY')}
              </span>
            ) : null}
          </span>
        ) : null}
      </div>
    </article>
  )
}
